import type {
  Candidate,
  Taxonomy,
  TaxonomyBusinessTitle,
  TaxonomyBusinessTitleEnum,
  TaxonomyFocusAreaEnum,
  TaxonomyPracticeAreaEnum,
  TaxonomySkillGroupEnum,
} from '@axiom/validation';
import { TaxonomyConst } from '@axiom/const';

export type TaxonomyPracticeAreaAndFocusAreaMixedOption =
  | {
      value: TaxonomyPracticeAreaEnum;
      type: 'PracticeArea';
      label: string;
    }
  | {
      value: TaxonomyFocusAreaEnum;
      type: 'FocusArea';
      label: string;
    };

export const TaxonomyUtil = {
  getGeneralSkillEnums: (generalSkills: Taxonomy['generalSkills']) => {
    return Object.keys(generalSkills).sort();
  },

  getGeneralSkillOptions: (generalSkills: Taxonomy['generalSkills']) => {
    return TaxonomyUtil.getGeneralSkillEnums(generalSkills).map(key => {
      const generalSkill = generalSkills[
        key
      ] as Taxonomy['generalSkills'][string];
      return {
        label: generalSkill.displayName,
        value: key,
      };
    });
  },

  getIndustryEnums: (industries: Taxonomy['industries']) => {
    return Object.keys(industries).sort();
  },

  getIndustryOptions: (industries: Taxonomy['industries']) => {
    return TaxonomyUtil.getIndustryEnums(industries).map(key => {
      const industry = industries[key] as Taxonomy['industries'][string];
      return {
        label: industry.displayValue,
        value: key,
      };
    });
  },

  getFilteredIndustryOptions: (
    industries: Taxonomy['industries']
  ): Array<{ label: string; value: string }> => {
    return TaxonomyUtil.getIndustryEnums(industries)
      .filter(opt => opt !== 'NA')
      .map(key => {
        const industry = industries[key] as Taxonomy['industries'][string];
        return {
          label: industry.displayValue,
          value: key,
        };
      });
  },

  getFilteredIntakeFormIndustryOptions: (
    industries: Taxonomy['industries']
  ) => {
    return TaxonomyUtil.getIndustryEnums(industries)
      .filter(opt => opt !== 'NA' && opt !== 'Other')
      .map(key => {
        const industry = industries[key] as Taxonomy['industries'][string];
        return {
          label: industry.displayValue,
          value: key,
        };
      });
  },

  getFocusAreaEnumsByPracticeAreaEnumForCandidate: (
    taxonomy: Taxonomy,
    candidate: Candidate
  ) => {
    const isAlp = !TaxonomyUtil.isLawyer(candidate.businessTitle);
    return Object.entries(taxonomy.focusAreas)
      .filter(([_, value]) => value.selectable)
      .reduce<Record<TaxonomyPracticeAreaEnum, TaxonomyFocusAreaEnum[]>>(
        (acc, [focusArea, { practiceArea, alpEligible }]) => ({
          ...acc,
          ...(!isAlp || alpEligible
            ? {
                [practiceArea]: [...(acc[practiceArea] ?? []), focusArea],
              }
            : {}),
        }),
        {}
      );
  },

  getSelectablePracticeAreaEnums: (
    practiceAreas: Taxonomy['practiceAreas']
  ) => {
    return Object.keys(practiceAreas)
      .filter(key => practiceAreas[key]?.selectable)
      .sort();
  },

  getPracticeAreaOptions: (practiceAreas: Taxonomy['practiceAreas']) => {
    return TaxonomyUtil.getSelectablePracticeAreaEnums(practiceAreas)
      .map(key => {
        const practiceArea = practiceAreas[
          key
        ] as Taxonomy['practiceAreas'][string];
        return {
          label: practiceArea.fullName,
          value: key,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  getFocusAreaOptions: (focusAreas: Taxonomy['focusAreas']) => {
    return TaxonomyUtil.getSelectablePracticeAreaEnums(focusAreas)
      .map(key => {
        const focusArea = focusAreas[key] as Taxonomy['focusAreas'][string];
        return {
          label: focusArea.fullName,
          value: key,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  getFocusAreaWithCategoryOptions: (focusAreas: Taxonomy['focusAreas']) => {
    return TaxonomyUtil.getSelectableFocusAreaEnums(focusAreas)
      .map(key => {
        const focusArea = focusAreas[key] as Taxonomy['focusAreas'][string];
        return {
          label: focusArea.fullName,
          value: key,
          practiceArea: focusArea.practiceArea,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  getSelectableFocusAreaEnums: (focusAreas: Taxonomy['focusAreas']) => {
    return Object.keys(focusAreas)
      .filter(key => focusAreas[key]?.selectable)
      .sort();
  },

  getPracticeAreasWithFocusAreaOptions: (
    focusAreas: Taxonomy['focusAreas'],
    practiceAreas: Taxonomy['practiceAreas']
  ) => {
    return TaxonomyUtil.getPracticeAreaOptions(practiceAreas).map(
      practiceArea => {
        return {
          label: practiceArea.label,
          value: `PA_${practiceArea.value}`,
          choices: TaxonomyUtil.getFocusAreaWithCategoryOptions(
            focusAreas
          ).filter(focusArea => focusArea.practiceArea === practiceArea.value),
        };
      }
    );
  },

  getPracticeAreaAndFocusAreaOptions: (
    practiceAreas: Taxonomy['practiceAreas'],
    focusAreas: Taxonomy['focusAreas']
  ) => {
    return TaxonomyUtil.getSelectablePracticeAreaEnums(practiceAreas)
      .reduce<TaxonomyPracticeAreaAndFocusAreaMixedOption[]>(
        (options, practiceAreaEnum) => {
          const practiceArea = practiceAreas[practiceAreaEnum];
          const focusAreasForPracticeAreas =
            TaxonomyUtil.getSelectableFocusAreaEnums(focusAreas)
              .filter(e => {
                const focusArea = focusAreas[e];
                return focusArea?.practiceArea === practiceAreaEnum;
              })
              .map(focusAreaEnum => {
                const focusArea = focusAreas[focusAreaEnum];
                return {
                  label: `${practiceArea?.fullName} - ${focusArea?.fullName}`,
                  value: focusAreaEnum,
                  type: 'FocusArea',
                };
              }) as TaxonomyPracticeAreaAndFocusAreaMixedOption[];
          return [
            ...options,
            {
              label: practiceArea?.fullName,
              value: practiceAreaEnum,
              type: 'PracticeArea',
            } as TaxonomyPracticeAreaAndFocusAreaMixedOption,
            ...focusAreasForPracticeAreas,
          ];
        },
        []
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  convertPracticeAreaEnumsToOptionLabels: (
    practiceAreas: Taxonomy['practiceAreas'],
    enums: Array<keyof Taxonomy['practiceAreas']>
  ): string[] => {
    const options = TaxonomyUtil.getPracticeAreaAndFocusAreaOptions(
      practiceAreas,
      {}
    );
    return enums
      .map(
        e =>
          options.find(opt => opt.value === e && opt.type === 'PracticeArea')
            ?.label
      )
      .filter(e => !!e)
      .sort() as string[];
  },

  convertFocusAreaEnumsToOptionLabels: (
    practiceAreas: Taxonomy['practiceAreas'],
    focusAreas: Taxonomy['focusAreas'],
    enums: Array<keyof Taxonomy['focusAreas']>
  ): string[] => {
    const options = TaxonomyUtil.getPracticeAreaAndFocusAreaOptions(
      practiceAreas,
      focusAreas
    );
    return enums
      .map(
        e =>
          options.find(opt => opt.value === e && opt.type === 'FocusArea')
            ?.label
      )
      .filter(e => !!e)
      .sort() as string[];
  },

  getFocusAreaSkillGroups: (
    focusArea: string,
    skillGroups: Taxonomy['skillGroups']
  ) => {
    return TaxonomyUtil.getSkillGroupEnums(skillGroups)
      .map(key => ({
        key,
        ...skillGroups[key],
      }))
      .filter(skillGroup => skillGroup.focusArea === focusArea);
  },

  getSkillGroupEnums: (skillGroups: Taxonomy['skillGroups']) => {
    return Object.keys(skillGroups);
  },

  getLegalSkillEnums: (legalSkills: Taxonomy['legalSkills']) => {
    return Object.keys(legalSkills);
  },

  getLegalSkillOptions: (
    legalSkills: Taxonomy['legalSkills'],
    skillGroup: string
  ) => {
    return TaxonomyUtil.getLegalSkillEnums(legalSkills)
      .map(key => ({
        key,
        ...legalSkills[key],
      }))
      .filter(legalSkill => legalSkill.skillGroup === skillGroup)
      .map(legalSkill => ({
        label: legalSkill.fullName ?? '',
        value: legalSkill.key,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  getLegalSkillsByFocusAreaOptions: (taxonomy: Taxonomy) => {
    return TaxonomyUtil.getSelectableFocusAreaEnums(taxonomy.focusAreas)
      .map(focusAreaEnum => ({
        label: taxonomy.focusAreas[focusAreaEnum]?.fullName ?? '',
        value: focusAreaEnum,
        choices: Object.entries(taxonomy.skillGroups)
          .filter(([, { focusArea }]) => focusArea === focusAreaEnum)
          .flatMap(([skillGroupEnum]) =>
            Object.entries(taxonomy.legalSkills)
              .filter(([, { skillGroup }]) => skillGroup === skillGroupEnum)
              .map(([legalSkillEnum]) => ({
                label: taxonomy.legalSkills[legalSkillEnum]?.fullName ?? '',
                value: legalSkillEnum,
              }))
          )
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  },

  getLegalTechSkillEnums: (legalTechSkills: Taxonomy['legalTechSkills']) => {
    return Object.keys(legalTechSkills);
  },

  getLegalTechSkillOptions: (legalTechSkills: Taxonomy['legalTechSkills']) => {
    return TaxonomyUtil.getLegalTechSkillEnums(legalTechSkills).map(key => {
      const legalTechSkill = legalTechSkills[
        key
      ] as Taxonomy['legalTechSkills'][string];
      return {
        label: legalTechSkill.displayName,
        value: key,
      };
    });
  },

  getLegalTechSkillCategoryEnums: (
    legalTechSkillCategories: Taxonomy['legalTechSkillCategories']
  ) => {
    return Object.keys(legalTechSkillCategories);
  },

  getLegalTechSkillByCategoryOptions: (
    legalTechSkills: Taxonomy['legalTechSkills'],
    legalTechSkillCategories: Taxonomy['legalTechSkillCategories']
  ) => {
    const filterableLegalTechSkills = TaxonomyUtil.getLegalTechSkillEnums(
      legalTechSkills
    ).map(key => {
      const legalTechSkill = legalTechSkills[
        key
      ] as Taxonomy['legalTechSkills'][string];
      return {
        label: legalTechSkill.displayName,
        value: key,
        category: legalTechSkill.legalTechSkillCategory,
      };
    });

    return TaxonomyUtil.getLegalTechSkillCategoryEnums(
      legalTechSkillCategories
    ).map(key => {
      const legalSkillCategory = legalTechSkillCategories[
        key
      ] as Taxonomy['legalTechSkillCategories'][string];
      return {
        label: legalSkillCategory.displayName,
        value: key,
        choices: filterableLegalTechSkills
          .filter(legalTechSkill => legalTechSkill.category === key)
          .map(legalTechSkill => ({
            label: legalTechSkill.label,
            value: legalTechSkill.value,
          })),
      };
    });
  },

  getLegalSkillsBySkillGroup: (
    skillGroup: TaxonomySkillGroupEnum,
    legalSkills: Taxonomy['legalSkills']
  ) => {
    return TaxonomyUtil.getLegalSkillEnums(legalSkills)
      .map(key => ({
        key,
        ...legalSkills[key],
      }))
      .filter(legalSkill => legalSkill.skillGroup === skillGroup);
  },

  formatPracticeAreaFocusAreaForDisplay(
    taxonomy: Taxonomy,
    primaryPracticeArea: string | null | undefined,
    primaryFocusArea: string | null | undefined
  ) {
    const { practiceAreas, focusAreas } = taxonomy;

    const practiceArea = primaryPracticeArea
      ? practiceAreas[primaryPracticeArea]?.fullName
      : null;
    const focusArea = primaryFocusArea
      ? focusAreas[primaryFocusArea]?.fullName
      : null;

    return practiceArea && focusArea && practiceArea !== focusArea
      ? `${practiceArea} - ${focusArea}`
      : practiceArea || null;
  },

  formatPracticeAreaFocusAreaForDisplayWithStandaloneDisplayName(
    taxonomy: Taxonomy,
    primaryPracticeArea: string | null | undefined,
    primaryFocusArea: string | null | undefined
  ) {
    const { practiceAreas, focusAreas } = taxonomy;

    const practiceArea = primaryPracticeArea
      ? practiceAreas[primaryPracticeArea]?.fullName
      : null;
    const focusArea = primaryFocusArea
      ? focusAreas[primaryFocusArea]?.standaloneDisplayName
      : null;

    return focusArea ?? practiceArea ?? null;
  },

  getBusinessTitleEnums(businessTitles: Taxonomy['businessTitles']) {
    const lawyer = TaxonomyConst.LawyerBusinessTitle;
    return [
      lawyer,
      ...Object.keys(businessTitles)
        .filter(title => title !== lawyer)
        .sort(),
    ];
  },

  getBusinessTitleOptions(businessTitles: Taxonomy['businessTitles']) {
    return TaxonomyUtil.getBusinessTitleEnums(businessTitles).map(
      businessTitle => ({
        label: (
          businessTitles[businessTitle] as Taxonomy['businessTitles'][string]
        ).displayName,
        value: businessTitle,
      })
    );
  },

  formatBusinessTitlesForDropdown({ businessTitles }: Taxonomy) {
    const keys = Object.keys(businessTitles).sort();
    const foundIndex = keys.indexOf(TaxonomyConst.LawyerBusinessTitle);

    if (foundIndex !== -1) {
      keys.splice(foundIndex, 1);
      keys.unshift(TaxonomyConst.LawyerBusinessTitle);
    }

    return keys.reduce<
      Array<{
        label: TaxonomyBusinessTitle['displayName'];
        value: TaxonomyBusinessTitleEnum;
      }>
    >((crnt, key: TaxonomyBusinessTitleEnum) => {
      if (businessTitles[key]?.displayName) {
        crnt.push({
          label: businessTitles[key].displayName,
          value: key,
        });
      }
      return crnt;
    }, []);
  },

  getBusinessTitle(
    taxonomy: Taxonomy,
    businessTitle: TaxonomyBusinessTitleEnum | null
  ): TaxonomyBusinessTitle | null {
    return taxonomy.businessTitles[businessTitle ?? ''] ?? null;
  },

  isLawyer(businessTitle: TaxonomyBusinessTitleEnum | null): boolean {
    return businessTitle === TaxonomyConst.LawyerBusinessTitle;
  },

  filterSelectablePracticeAreas(
    taxonomy: Taxonomy,
    enums: Array<keyof Taxonomy['practiceAreas']>
  ) {
    const practiceAreaEnums = this.getSelectablePracticeAreaEnums(
      taxonomy.practiceAreas
    );

    return enums.filter(e => practiceAreaEnums.includes(e));
  },

  filterSelectableFocusAreas(
    taxonomy: Taxonomy,
    enums: Array<keyof Taxonomy['focusAreas']>
  ) {
    const focusAreaEnums = this.getSelectableFocusAreaEnums(
      taxonomy.focusAreas
    );

    return enums.filter(e => focusAreaEnums.includes(e));
  },

  isPracticeAreaSelectable(
    taxonomy: Taxonomy,
    practiceAreaEnum?: keyof Taxonomy['practiceAreas'] | null
  ) {
    const practiceAreaEnums = this.getSelectablePracticeAreaEnums(
      taxonomy.practiceAreas
    );

    return practiceAreaEnums.includes(practiceAreaEnum);
  },

  isFocusAreaSelectable(
    taxonomy: Taxonomy,
    focusAreaEnum?: keyof Taxonomy['focusAreas'] | null
  ) {
    const focusAreaEnums = this.getSelectableFocusAreaEnums(
      taxonomy.focusAreas
    );

    return focusAreaEnums.includes(focusAreaEnum);
  },
};
